import {
  ITmpSession,
  ITmpSessionEvent,
  TTmpProvisioningStatus,
  TTmpSessionEventKind,
  TTmpSessionEventSource,
} from "../../../@types/admin/AdminTmpIntegration";
import { getLongFormattedDate } from "../../../utils/dateFormatter";

export type TTmpTone = "active" | "inactive" | "warning" | "error";

/** Plain-language name of each provisioning status, for badges and the filter. */
export const PROVISIONING_STATUS_LABELS: Record<
  TTmpProvisioningStatus,
  string
> = {
  PendingMapping: "Needs the doctor's staff profile",
  PendingData: "Waiting for the doctor",
  PendingReactivation: "Waiting for reactivation",
  Ready: "Queued",
  AcceptPending: "Confirming with TMP",
  Provisioned: "Active",
  Failed: "Failed",
  Rejected: "Rejected",
  Cancelled: "Cancelled",
  Ended: "Ended",
};

export function provisioningStatusLabel(status: TTmpProvisioningStatus) {
  return PROVISIONING_STATUS_LABELS[status] ?? status;
}

/** Green once provisioned, red when the provisioner gave up, amber while waiting on people, grey while waiting on machines. */
export function provisioningStatusTone(
  status: TTmpProvisioningStatus | string
): TTmpTone {
  switch (status) {
    case "Provisioned":
      return "active";
    case "Failed":
    case "Rejected":
      return "error";
    case "PendingData":
    case "PendingMapping":
    case "PendingReactivation":
      return "warning";
    default:
      // Ready / AcceptPending: the job runs on its own. Cancelled / Ended:
      // TMP withdrew the order; nothing to do.
      return "inactive";
  }
}

const FIELD_LABELS: Record<string, string> = {
  firstName: "first name",
  lastName: "last name",
  email: "email",
  birthDate: "date of birth",
  gender: "gender",
};

export function fieldList(fields: string[]): string {
  return fields.map((f) => FIELD_LABELS[f] ?? f).join(", ");
}

/**
 * What happens next with the order and who has to act, in one sentence. The
 * list shows it as "Next step"; the drill-down shows it under the status.
 */
export function nextStep(session: ITmpSession): {
  text: string;
  tone: TTmpTone;
} {
  const retryAt = session.nextAttemptAt
    ? ` Next try ${getLongFormattedDate(session.nextAttemptAt, "")}.`
    : "";

  switch (session.provisioningStatus) {
    case "PendingMapping":
      return {
        text: session.responsibleHcpHealthProviderId
          ? `Add Health provider ID ${session.responsibleHcpHealthProviderId} to the doctor's staff profile, then retry.`
          : "TMP sent no responsible HCP; the order cannot be routed.",
        tone: "warning",
      };
    case "PendingData":
      return {
        text: `Doctor must complete the form (missing ${fieldList(
          session.missingFields
        )}).`,
        tone: "warning",
      };
    case "PendingReactivation":
      return {
        text: "Doctor must confirm the reactivation on the form.",
        tone: "warning",
      };
    case "Ready":
      return {
        text: session.lastError
          ? `Retrying patient creation.${retryAt}`
          : "The job creates the patient on its next run.",
        tone: "inactive",
      };
    case "AcceptPending":
      return {
        text: session.lastError
          ? `Waiting for TMP to take the "accepted" status.${retryAt}`
          : 'Patient ready; confirming "accepted" to TMP.',
        tone: "inactive",
      };
    case "Provisioned":
      return {
        text: "Nothing to do. The patient is monitored.",
        tone: "active",
      };
    case "Failed":
      return { text: "Fix the cause below, then retry.", tone: "error" };
    case "Rejected":
      // TMP already showed the doctor a failed order, so it is never retried.
      return {
        text: "Refused and reported to TMP. Fix the cause, then the doctor sends a new order.",
        tone: "error",
      };
    case "Ended":
      return session.canRetry
        ? {
            text: "The stop is not finished on our side. Retry it.",
            tone: "error",
          }
        : { text: "Stopped in the EHR. Patient archived.", tone: "inactive" };
    case "Cancelled":
      return {
        text: "Cancelled in the EHR before a patient was created.",
        tone: "inactive",
      };
    default:
      return { text: "—", tone: "inactive" };
  }
}

/** The most recent moment anything happened to the order. */
export function lastActivity(session: ITmpSession): string {
  return [
    session.created,
    session.lastAttemptedAt,
    session.provisionedAt,
    session.acceptedAt,
    session.lastActionAt,
    session.statusConfirmedAt,
  ]
    .filter((v): v is string => Boolean(v))
    .reduce((latest, v) => (v > latest ? v : latest), session.created);
}

const EVENT_LABELS: Record<TTmpSessionEventKind, string> = {
  Received: "Order received from TMP",
  Redelivered: "TMP sent the order again",
  WardAssigned: "Ward chosen",
  WardRuleMissing: "No staff member for the doctor",
  WaitingForData: "Waiting for the doctor's form",
  ReadyToProvision: "Ready to create the patient",
  Rejected: "Order rejected",
  ReactivationRequested: "Archived patient found; reactivation requested",
  DataCompleted: "Doctor completed the form",
  ReactivationConfirmed: "Doctor confirmed the reactivation",
  LinkRequested: "New completion link requested",
  LinkSent: "Completion link sent to TMP",
  PatientCreated: "Patient created",
  PatientLinked: "Linked to existing patient",
  PatientReactivated: "Patient reactivated",
  Accepted: 'TMP confirmed "accepted"',
  AttemptFailed: "Attempt failed; will retry",
  AttemptDeferred: "TMP not reachable; waiting",
  Failed: "Failed",
  RetryRequested: "Retried by an admin",
  StopReceived: "Stopped in the EHR",
  CancelReceived: "Cancelled in the EHR",
  PatientArchived: "Patient archived",
  ArchiveFailed: "Could not archive the patient",
  StatusConfirmed: "TMP confirmed the stop",
  StatusConfirmationFailed: "TMP did not take the stop status",
  ArchiveSkipped: "Patient not archived: a newer order owns them",
};

export function eventLabel(event: ITmpSessionEvent): string {
  if (event.kind === "StatusConfirmed" && event.tmpStatus === "cancelled") {
    return "TMP confirmed the cancellation";
  }
  return EVENT_LABELS[event.kind] ?? event.kind;
}

export function eventTone(kind: TTmpSessionEventKind): TTmpTone {
  switch (kind) {
    case "PatientCreated":
    case "PatientLinked":
    case "PatientReactivated":
    case "Accepted":
    case "PatientArchived":
    case "StatusConfirmed":
    case "DataCompleted":
    case "ReactivationConfirmed":
      return "active";
    case "Failed":
    case "Rejected":
    case "ArchiveFailed":
    case "StatusConfirmationFailed":
      return "error";
    case "AttemptFailed":
    case "AttemptDeferred":
    case "ArchiveSkipped":
    case "WardRuleMissing":
    case "WaitingForData":
    case "ReactivationRequested":
      return "warning";
    default:
      return "inactive";
  }
}

export const EVENT_SOURCE_LABELS: Record<TTmpSessionEventSource, string> = {
  Tmp: "TMP",
  Hcp: "Doctor",
  Admin: "Admin",
  Pmp: "pMp",
};
